import { useState, useMemo, useCallback } from 'react'
import { ModalProps } from '../../types/modal'
import Modal from '../shared/Modal'
import { SIDE_FILTER_KEY, SideFilterForm } from './SideFilterFormModal'
import { ValueFilterForm } from './ValueFilterFormModal'
import { TraderFilterForm } from './TraderFilterFormModal'
import { VenueFilterForm } from './VenueFilterFormModal'
import Button, { IconButton } from '@components/shared/Button'
import { ArrowLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { useLocalStorage, LocalStorageKey } from 'hooks/useLocalStorageState'
import { formatNumericValue } from 'utils/numbers'
import {
  TRADE_VALUE_FILTER_KEY,
  TRADER_ADDRESS_FILTER_KEY,
  DEFAULT_TRADE_VALUE_FILTER,
} from 'utils/constants'
import { useVenueFilter, VenueFilter } from '@components/trade/useSettings'
import { useTranslation } from 'next-i18next'
import { SideFilter } from 'hooks/useRecentTrades'

const FILTERS = ['side', 'value', 'trader', 'venue'] as const
type Filter = (typeof FILTERS)[number] | ''

type LocalStorageKeyType<T> = T extends LocalStorageKey<infer W> ? W : never

export function calcFilterCount(
  addressToFilter: LocalStorageKeyType<typeof TRADER_ADDRESS_FILTER_KEY>,
  sideFilter: SideFilter,
  valueFilter: typeof DEFAULT_TRADE_VALUE_FILTER,
  venueFilter: VenueFilter,
) {
  let count = 0

  const hasAddressFilter = !!addressToFilter.length
  if (hasAddressFilter) count += 1

  const hasSideFilter = !sideFilter.buy || !sideFilter.sell
  if (hasSideFilter) count += 1

  const hasValueFilter = Object.values(valueFilter).some(
    (value) => value.min !== '' || value.max !== '',
  )
  if (hasValueFilter) count += 1

  const hasVenueFilter = Object.values(venueFilter).some((value) => !value)
  if (hasVenueFilter) count += 1

  return count
}

const MobileTradesFilterModal = ({ isOpen, onClose }: ModalProps) => {
  const { t } = useTranslation('trade')
  const [activeFilterPage, setActiveFilterPage] = useState<Filter>('')
  const [addressToFilter, setAddressToFilter] = useLocalStorage(
    TRADER_ADDRESS_FILTER_KEY,
  )
  const [valueFilter, setValueFilter] = useLocalStorage(TRADE_VALUE_FILTER_KEY)
  const [venueFilter, setVenueFilter] = useVenueFilter()
  const [sideFilter, setSideFilter] = useLocalStorage(SIDE_FILTER_KEY)

  const filterCount = useMemo(
    () =>
      calcFilterCount(addressToFilter, sideFilter, valueFilter, venueFilter),
    [addressToFilter, sideFilter, valueFilter, venueFilter],
  )

  const clearFilters = useCallback(() => {
    setValueFilter(DEFAULT_TRADE_VALUE_FILTER)
    setAddressToFilter([])
    setVenueFilter(
      Object.fromEntries(
        Object.keys(venueFilter).map((venue) => [venue, true]),
      ),
    )
    setSideFilter({ buy: true, sell: true })
    onClose()
  }, [
    onClose,
    setAddressToFilter,
    setSideFilter,
    setValueFilter,
    setVenueFilter,
    venueFilter,
  ])

  const getFilterDesc = (filter: Filter) => {
    if (filter === 'side') {
      if (!sideFilter.buy && sideFilter.sell) {
        return t('filter.mobile.sells-only')
      }
      if (!sideFilter.sell && sideFilter.buy) {
        return t('filter.mobile.buys-only')
      }
      return t('filter.mobile.both')
    }
    if (filter === 'value') {
      if (!valueFilter.USDC.min) return t('filter.mobile.all')
      return `$${formatNumericValue(valueFilter.USDC.min)} > ${
        valueFilter.USDC.max
          ? `$${formatNumericValue(valueFilter.USDC.max)}`
          : 'Infinity'
      }`
    }
    if (filter === 'trader') {
      return addressToFilter.length
        ? addressToFilter.length
        : t('filter.mobile.all')
    }
    if (filter === 'venue') {
      return Object.values(venueFilter).filter((value) => value).length
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <>
        <div
          className={`mb-4 flex items-center ${
            activeFilterPage ? 'justify-between' : 'justify-center'
          }`}
        >
          {activeFilterPage ? (
            <IconButton hideBg onClick={() => setActiveFilterPage('')}>
              <ArrowLeftIcon className="size-5" />
            </IconButton>
          ) : null}
          <h2 className="text-center">{t('filter.title.trades')}</h2>
          {activeFilterPage ? <div className="size-5" /> : null}
        </div>
        {!activeFilterPage ? (
          <div className="flex flex-col items-end">
            <div className="w-full space-y-2">
              {FILTERS.map((filter) => (
                <button
                  className="flex w-full justify-between rounded-md border border-th-bkg-4 px-4 py-3 text-left text-th-fgd-1"
                  onClick={() => setActiveFilterPage(filter)}
                  key={filter}
                >
                  <span>{t(`filter.mobile.${filter}`)}</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-th-fgd-4">
                      {getFilterDesc(filter)}
                    </span>
                    <ChevronRightIcon className="size-5 text-th-fgd-3" />
                  </div>
                </button>
              ))}
            </div>
            <div className="mt-6 flex flex-row items-end gap-2">
              {filterCount > 0 ? (
                <Button onClick={clearFilters} secondary>
                  {t('filter.mobile.clear-all', { count: filterCount })}
                </Button>
              ) : null}
              <Button onClick={onClose}>{t('filter.mobile.done')}</Button>
            </div>
          </div>
        ) : (
          <FilterContent
            filter={activeFilterPage}
            onSave={() => setActiveFilterPage('')}
          />
        )}
      </>
    </Modal>
  )
}

export default MobileTradesFilterModal

const FilterContent = ({
  filter,
  onSave,
}: {
  filter: Filter
  onSave: () => void
}) => {
  switch (filter) {
    case 'value':
      return <ValueFilterForm onSave={onSave} />
    case 'trader':
      return <TraderFilterForm onSave={onSave} />
    case 'venue':
      return <VenueFilterForm onSave={onSave} />
    case 'side':
    default:
      return <SideFilterForm onSave={onSave} />
  }
}
